/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { PriceComponent } from '@msdyn365-commerce/components';
import {
    IComponent,
    IComponentProps,
    ICoreContext,
    IGridSettings,
    IImageData,
    IImageSettings,
    Image,
    IRequestContext
} from '@msdyn365-commerce/core';
import { ProductPrice, ProductSearchResult } from '@msdyn365-commerce/retail-proxy';
import React from 'react';

/**
 * IActiveProductProps interface.
 */
export interface IActiveProductProps extends IComponentProps<{ product?: ProductSearchResult; url?: string }> {
    className?: string;
    imageSettings?: IImageSettings;
    savingsText?: string;
    freePriceText?: string;
    originalPriceText?: string;
    currentPriceText?: string;
    seeMoreButtonText?: string;
}

/**
 * IActiveProduct interface.
 */
export interface IActiveProduct extends IComponent<IActiveProductProps> {}

/**
 * RenderPrice function.
 * @param context - The core context.
 * @param typeName - The type name of the module.
 * @param productId - The product id.
 * @param basePrice - The base price of the product.
 * @param adjustedPrice - The adjusted price of the product.
 * @param savingsText - The savings text of the product.
 * @param freePriceText - The free price text.
 * @param originalPriceText - The original price text.
 * @returns - Returns JSX.Element | null.
 */
function renderPrice(
    context: ICoreContext,
    typeName: string,
    productId: string,
    basePrice?: number,
    adjustedPrice?: number,
    savingsText?: string,
    freePriceText?: string,
    originalPriceText?: string
): JSX.Element | null {
    const price: ProductPrice = {
        BasePrice: basePrice,
        AdjustedPrice: adjustedPrice,
        CustomerContextualPrice: adjustedPrice
    };

    return (
        <PriceComponent
            context={context}
            id={productId}
            typeName={typeName}
            data={{ price }}
            savingsText={savingsText}
            freePriceText={freePriceText}
            originalPriceText={originalPriceText}
        />
    );
}

/**
 * RenderProductPlacementImage function.
 * @param requestContext - RequestContext.
 * @param imageSettings - The product image settings.
 * @param gridSettings - The viewport gird settings.
 * @param imageUrl - The product image url.
 * @param altText - The product image alt text.
 * @returns - Returns JSX.Element | null.
 */
function renderProductPlacementImage(
    requestContext: IRequestContext,
    imageSettings?: IImageSettings,
    gridSettings?: IGridSettings,
    imageUrl?: string,
    altText?: string
): JSX.Element | null {
    if (!imageUrl || !gridSettings || !imageSettings) {
        return null;
    }
    const imgData: IImageData = {
        src: imageUrl,
        altText: altText ? altText : ''
    };
    const imageProps = {
        gridSettings,
        imageSettings
    };
    imageProps.imageSettings.cropFocalRegion = true;
    return <Image {...imgData} {...imageProps} requestContext={requestContext} loadFailureBehavior='empty' />;
}

/**
 * ActiveProduct constant.
 * @param props - The active product props.
 * @param props.data - The product search result data.
 * @param props.context - The core context.
 * @param props.imageSettings - The prodcut image settings.
 * @param props.savingsText - The product savings text.
 * @param props.freePriceText - The free price text.
 * @param props.originalPriceText - The original price text.
 * @param props.ratingAriaLabel - The product rating aria label.
 * @param props.typeName - The type name of the module.
 * @param props.id - The id of the product.
 * @returns - Returns JSX.Element | null.
 */
export const ActiveProductComponent: React.FC<IActiveProductProps> = ({
    data,
    context,
    imageSettings,
    savingsText,
    freePriceText,
    originalPriceText,
    seeMoreButtonText,
    typeName,
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Do not need naming convention check for library prameters
    id
}) => {
    const { product, url } = data;
    if (!product) {
        return null;
    }

    return (
        <div className='msc-active-image-product'>
            <div className='msc-active-image-product__image'>
                {renderProductPlacementImage(
                    context.actionContext.requestContext,
                    imageSettings,
                    context.request.gridSettings,
                    product.PrimaryImageUrl,
                    product.Name
                )}
            </div>
            <div className='msc-active-image-product__details'>
                <h4 className='msc-active-image-product__title'>{product.Name}</h4>
                <div>
                    {renderPrice(context, typeName, id, product.BasePrice, product.Price, savingsText, freePriceText, originalPriceText)}
                    <a className='msc-add-to-cart' href={url}>
                        {seeMoreButtonText}
                    </a>
                </div>
            </div>
        </div>
    );
};

export default ActiveProductComponent;
